import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './TypingSpeed.css';

const sampleTexts = [
  "The quick brown fox jumps over the lazy dog.",
  "Pack my box with five dozen liquor jugs.",
  "How vexingly quick daft zebras jump!",
  "Waltz, bad nymph, for quick jigs vex.",
  "Sphinx of black quartz, judge my vow."
];

const TypingSpeed = () => {
  const [currentText, setCurrentText] = useState('');
  const [userInput, setUserInput] = useState('');
  const [timeLeft, setTimeLeft] = useState(60);
  const [gameActive, setGameActive] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [wpm, setWpm] = useState(0);
  const [accuracy, setAccuracy] = useState(100);
  const [completedSentences, setCompletedSentences] = useState(0);

  const getRandomText = () => {
    return sampleTexts[Math.floor(Math.random() * sampleTexts.length)];
  };

  const startGame = () => {
    const randomText = getRandomText();
    setCurrentText(randomText);
    setUserInput('');
    setTimeLeft(60);
    setGameActive(true);
    setGameOver(false);
    setWpm(0);
    setAccuracy(100);
    setCompletedSentences(0);
  };

  useEffect(() => {
    if (gameActive) {
      const totalCharsTyped = completedSentences * 50 + userInput.length; // Estimate 50 chars per sentence
      const timeElapsed = 60 - timeLeft;
      const currentWpm = timeElapsed > 0 ? Math.round((totalCharsTyped / 5 / timeElapsed) * 60) : 0;
      
      let correctChars = 0;
      for (let i = 0; i < userInput.length; i++) {
        if (userInput[i] === currentText[i]) {
          correctChars++;
        }
      }
      const currentAccuracy = userInput.length > 0 ? Math.round((correctChars / userInput.length) * 100) : 100;
      
      setWpm(currentWpm);
      setAccuracy(currentAccuracy);
    }
  }, [userInput, timeLeft, gameActive, completedSentences, currentText]);

  useEffect(() => {
    let timer;
    if (gameActive && timeLeft > 0) {
      timer = setTimeout(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
    } else if (timeLeft === 0 && gameActive) {
      setGameActive(false);
      setGameOver(true);
    }
    
    return () => clearTimeout(timer);
  }, [timeLeft, gameActive]);

  const handleInputChange = (e) => {
    if (!gameActive) return;
    const newInput = e.target.value;
    
    // Check if sentence is completed
    if (newInput === currentText) {
      setCompletedSentences(prev => prev + 1);
      // Load new sentence
      const newText = getRandomText();
      setCurrentText(newText);
      setUserInput('');
    } else {
      setUserInput(newInput);
    }
  };

  const renderText = () => {
    return currentText.split('').map((char, index) => {
      let className = 'char';
      if (index < userInput.length) {
        className += userInput[index] === char ? ' correct' : ' incorrect';
      } else if (index === userInput.length) {
        className += ' current';
      }
      return (
        <span key={index} className={className}>
          {char}
        </span>
      );
    });
  };

  return (
    <div className="game-container">
      <div className="game-header">
        <h1 className="game-title-header">⌨️ Typing Speed Test</h1>
        <div className="game-instructions">
          Type the text as quickly and accurately as possible!
        </div>
      </div>

      <div className="game-board">
        <div className="score-display">
          WPM: {wpm} | Accuracy: {accuracy}% | Time: {timeLeft}s | Completed: {completedSentences}
        </div>
        
        {!gameActive && !gameOver && (
          <div className="start-screen">
            <p>Ready to test your typing speed?</p>
            <button className="btn btn-success" onClick={startGame}>
              Start Test
            </button>
          </div>
        )}

        {gameActive && (
          <div className="typing-area">
            <div className="text-display">
              {renderText()}
            </div>
            
            <textarea
              value={userInput}
              onChange={handleInputChange}
              placeholder="Start typing here..."
              className="typing-input"
              autoFocus
            />
          </div>
        )}

        {gameOver && (
          <div className="game-over">
            <h2>⏰ Time's Up!</h2>
            <div className="final-stats">
              <p>Words Per Minute: {wpm}</p>
              <p>Accuracy: {accuracy}%</p>
              <p>Sentences Completed: {completedSentences}</p>
              <p>
                {wpm >= 60 ? '🏆 Speed Demon!' : 
                 wpm >= 40 ? '� Greabt job!' : 
                 wpm >= 25 ? '👌 Not bad!' : '💪 Keep practicing!'}
              </p>
            </div>
          </div>
        )}

        <div className="game-controls">
          <button className="btn btn-primary" onClick={startGame}>
            {gameOver ? 'Try Again' : 'New Test'}
          </button>
          <Link to="/" className="btn btn-secondary">
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TypingSpeed;